// ============================================================================
// FSA NOTE MODAL — DISCLOSURE FIXED GRID
// ============================================================================
// Renders a disclosure whose spec carries `column_spec` + `fixed_rows`. Rows
// are predetermined (Opening / Add / Less / Closing patterns); only the
// data cells are editable. Computed rows (`is_computed: true` on the
// fixed_row) derive each data column from `row_<token>` references to
// other rows' values for the same column.
//
// Row tokens derive from row.row_key if set, else from row.particulars /
// row.category via deriveRowKey heuristic.

import React, { useCallback, useMemo } from 'react';
import { evaluateFormula, deriveRowKey } from './formula';
import { DisclosureGroupHeader } from './DisclosureGroupHeader';
import { evaluateValidations, type ValidationRule } from './validations';
import { DriftBadgeList } from './DriftBadgeList';
import { AmountCellEditor } from './AmountCellEditor';
import {
  autoRound,
  applyRoundingTolerance,
  factorUnitsLabel,
  DEFAULT_ROUNDING_TOLERANCE,
  ROUNDED_SUFFIX,
  type RoundingFactor,
} from '../../shared/rounding';

import { resolveColumnWidth, type ColumnSpec } from './DisclosureVariableGrid';

export interface FixedRow {
  row_key?: string;
  particulars?: string;
  category?: string;
  is_computed?: boolean;
  formula?: string;
  /**
   * Per-row override of the column's data_type for display purposes.
   * Used by Note 35 EPS where the disclosure has a single value-column
   * pair but individual rows hold different unit kinds (integer share
   * counts, amount profit, per-share EPS values). When set to 'integer',
   * the cell renders as a plain integer input — no rounded companion,
   * no factor unit hint.
   */
  data_type?: string;
  /**
   * When true, amount cells in this row skip the engagement rounding
   * factor — actual === rounded. Use for rows whose value isn't a
   * factor-applicable rupee amount (face value per share, EPS in Rs
   * per share, share counts).
   */
  rounding_exempt?: boolean;
  /**
   * When set, the row's value is pulled from a saved Statement line at
   * hydrate/save time (e.g. EPS profit row pulls from P&L
   * PL_PROFIT_FOR_PERIOD). Renders read-only — same lock as is_computed.
   */
  value_source?: { kind: string; report_code?: string; line_code?: string };
  /**
   * When true, the backend projects this row's value into class_data
   * under "<disclosure_code>.<row_key>" so the Statement evaluator can
   * resolve it via note_class_code. Display-side, this flag has no
   * effect — it's a marker for the backend projection step.
   */
  addressable?: boolean;
  [extra: string]: unknown;
}

type CellMap = Record<string, string | number | null>;
export type Row = CellMap;

/**
 * Returns the rounding factor that should apply to <paramref>row</paramref>'s
 * amount cells. The schedule-level engagement factor applies unless the row
 * declares it shouldn't:
 *   - `data_type: 'integer'` → 'ACTUALS' (integer counts aren't subject to
 *     unit factor; e.g. EPS share counts)
 *   - `rounding_exempt: true` → 'ACTUALS' (per-unit values like par value or
 *     EPS-rate aren't subject to unit factor)
 * Used by both the normalised-row seeder and the recompute pass so the
 * `__rounded` companion stored on save matches the value the auditor sees.
 * Without this, the saved snapshot diverges from the screen and PDF prints
 * the divided value (e.g. 40 shares → 0.04 in THOUSANDS) — violating the
 * "what is saved is what you see" contract.
 */
function effectiveFactorForRow(row: FixedRow, baseFactor: RoundingFactor): RoundingFactor {
  if (row?.rounding_exempt === true) return 'ACTUALS';
  if (row?.data_type === 'integer')  return 'ACTUALS';
  return baseFactor;
}

interface Props {
  columns: ColumnSpec[];
  fixedRows: FixedRow[];
  /** Stored grid value — one entry per fixed_row (in order). */
  rows: Row[];
  onChange: (rows: Row[]) => void;
  readOnly: boolean;
  /** JSON-declared cross-cell validation rules from the disclosure spec.
   *  Engine evaluates after every cell change; failing rules render as
   *  warning chips below the grid. Warn-only — never blocks save. */
  validations?: ValidationRule[];
  /** Engagement rounding-factor key — drives amount cells' auto-round
   *  companion. NULL falls back to ACTUALS (1:1, no divisor). */
  roundingFactor?: RoundingFactor | null;
  /** Engagement rounding tolerance — preserves user override on amount
   *  cells when within tolerance. */
  roundingTolerance?: number;
  /** Note-Modal-level Actual / Rounded toggle. Defaults to `'both'`. */
  displayMode?: 'actual' | 'rounded' | 'both';
  /**
   * Per-row live values for `value_source` rows on this disclosure,
   * keyed by row_key. Saved row values stay authoritative; this map
   * is used purely for drift detection — a small chip flags rows
   * whose saved value diverged from the source Statement's current
   * value. Refresh is triggered at the pane level via "Update from
   * Sources", not from inside this grid.
   */
  liveValuesByRowKey?: Record<string, { actualCy: number; roundedCy: number; actualPy: number; roundedPy: number }>;
}

const DisclosureFixedGrid: React.FC<Props> = ({
  columns,
  fixedRows,
  rows,
  onChange,
  readOnly,
  validations,
  roundingFactor,
  roundingTolerance,
  displayMode = 'both',
  liveValuesByRowKey,
}) => {
  const factor = roundingFactor ?? 'ACTUALS';
  const tolerance = roundingTolerance ?? DEFAULT_ROUNDING_TOLERANCE;
  const factorUnits = factorUnitsLabel(factor);
  // Identify which columns hold the row label (text columns) vs data columns
  // (amount / integer / percent / date / enum). Computed rows' formulas only
  // make sense for numeric data columns; label columns pass through verbatim
  // from the spec.
  const labelColumns = columns.filter(c => c.data_type === 'text' && /particular|category/i.test(c.code));
  const dataColumns = columns.filter(c => !labelColumns.includes(c));

  // Ensure the stored rows array matches the spec's fixed_rows order + count.
  // Missing rows seed from the spec's row entry; extras are dropped. Amount
  // columns also seed a `__rounded` companion (defaults to autoRound of the
  // existing actual when the stored row never carried one — handles dev DBs
  // migrating from single-value cells to dual-storage).
  const normalizedRows = useMemo(() => {
    return fixedRows.map((fr, i) => {
      const existing = rows[i] ?? {};
      const next: Row = {};
      for (const c of labelColumns) {
        next[c.code] = (fr[c.code] as string | undefined) ?? fr.particulars ?? fr.category ?? '';
      }
      for (const c of dataColumns) {
        const isNumeric = c.data_type === 'amount' || c.data_type === 'integer' || c.data_type === 'percent';
        const seed = isNumeric ? 0 : '';
        next[c.code] = existing[c.code] ?? seed;
        if (c.data_type === 'amount') {
          const actual = typeof next[c.code] === 'number' ? (next[c.code] as number) : 0;
          const storedRounded = existing[c.code + ROUNDED_SUFFIX];
          next[c.code + ROUNDED_SUFFIX] = typeof storedRounded === 'number'
            ? storedRounded
            : autoRound(actual, effectiveFactorForRow(fr, factor));
        }
      }
      return next;
    });
  }, [fixedRows, rows, labelColumns, dataColumns, factor]);

  // Build a per-column lookup of row_token → value, used by computed rows.
  const rowKeysInOrder = useMemo(() => fixedRows.map(fr => deriveRowKey(fr)), [fixedRows]);

  const handleCellChange = useCallback(
    (rowIdx: number, col: ColumnSpec, value: string | number | null) => {
      const next = normalizedRows.map((r, i) => (i === rowIdx ? { ...r, [col.code]: value } : r));
      onChange(recomputeFixedGrid(next, fixedRows, dataColumns, rowKeysInOrder, factor, tolerance));
    },
    [normalizedRows, fixedRows, dataColumns, rowKeysInOrder, onChange, factor, tolerance],
  );

  // Amount cells write BOTH the actual and the rounded companion in one
  // patch. Keeps the auto-round/tolerance behaviour centralised in the
  // AmountCellEditor; the grid just persists what the editor decided.
  const handleAmountCellChange = useCallback(
    (rowIdx: number, col: ColumnSpec, actual: number, rounded: number) => {
      const next = normalizedRows.map((r, i) =>
        i === rowIdx
          ? { ...r, [col.code]: actual, [col.code + ROUNDED_SUFFIX]: rounded }
          : r,
      );
      onChange(recomputeFixedGrid(next, fixedRows, dataColumns, rowKeysInOrder, factor, tolerance));
    },
    [normalizedRows, fixedRows, dataColumns, rowKeysInOrder, onChange, factor, tolerance],
  );

  // First render: if the stored shape diverged from the spec, persist the
  // normalized shape so downstream reads see consistent data. Two cases:
  //   1. Row count differs from the fixed-row spec (new rows added in seed).
  //   2. Amount columns lack their __rounded companion (legacy single-value
  //      rows). Without this, the PDF Rounded mode falls back to actual
  //      because nothing rounded was ever saved.
  //
  // Guard: `rows.length === 0` means EITHER the auditor hasn't saved this
  // disclosure yet OR hydrate hasn't completed (workspace state still
  // empty). In either case there's nothing to seed — and firing onChange
  // here races with hydrate's setWorkspace, clobbering the saved rows
  // that are mid-flight back from the API. The auditor sees an empty
  // grid on first open even though the data is in the response and the
  // DB. Skip until we have at least one saved row to operate on; the
  // first auditor cell-edit will fire its own recompute and persist the
  // canonical shape from there.
  React.useEffect(() => {
    if (rows.length === 0) return;
    const rowCountMismatch = rows.length !== normalizedRows.length;
    const missingRoundedCompanion = normalizedRows.some((nr, i) => {
      const stored = rows[i];
      if (!stored) return true; // count-mismatch case, handled above
      return dataColumns.some(c =>
        c.data_type === 'amount' && typeof stored[c.code + ROUNDED_SUFFIX] !== 'number',
      );
    });
    if (rowCountMismatch || missingRoundedCompanion) {
      onChange(recomputeFixedGrid(normalizedRows, fixedRows, dataColumns, rowKeysInOrder, factor, tolerance));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, normalizedRows.length]);

  // Run JSON-declared validations against the current grid state. Failing
  // rules render as warn-only chips below the table — they never block
  // save (per the FSA-2.2 decision: approver reviews warnings at sign-off).
  const validationResults = useMemo(
    () => evaluateValidations(validations ?? [], normalizedRows, rowKeysInOrder),
    [validations, normalizedRows, rowKeysInOrder],
  );

  return (
    <>
    <table style={gridTableStyle}>
      <colgroup>
        {columns.map(c => (
          <col key={c.code} style={{ width: resolveColumnWidth(c), minWidth: 80 }} />
        ))}
      </colgroup>
      <thead>
        <DisclosureGroupHeader
          columns={columns}
          cellStyle={thStyle}
          groupCellStyle={() => thGroupStyle}
          renderLabel={c => (
            <>
              {c.label}
              {c.is_computed && <span style={computedChipStyle}> ƒ</span>}
            </>
          )}
        />
      </thead>
      <tbody>
        {normalizedRows.map((row, i) => {
          const fr = fixedRows[i];
          const isComputedRow = fr.is_computed === true;
          // value_source rows (e.g. EPS profit pulled from P&L) lock
          // the same way is_computed rows do — auditor can't edit, the
          // backend owns the value.
          const isLockedRow = isComputedRow || !!fr.value_source;
          // Per-row data_type override — Note 35 EPS uses this so
          // share-count rows render as integers within an amount column.
          const rowDataType = typeof fr.data_type === 'string' ? fr.data_type : undefined;
          // Per-row rounding-exempt — bypass engagement factor for this
          // row's amount cells (face value, EPS rates, anything not in
          // factor-applicable rupees).
          const rowRoundingExempt = fr.rounding_exempt === true;
          // Drift detection: per-row, when this row carries a value_source
          // pull AND the saved value_cy / value_py differ from the live
          // source values. Pure display — refresh is auditor-triggered
          // via the pane-level "Update from Sources" button.
          const rowKeyForDrift = typeof fr.row_key === 'string' ? fr.row_key : undefined;
          const liveForRow = rowKeyForDrift ? liveValuesByRowKey?.[rowKeyForDrift] : undefined;
          const savedCy = typeof row.value_cy === 'number' ? row.value_cy : 0;
          const savedPy = typeof row.value_py === 'number' ? row.value_py : 0;
          const rowHasDrift = liveForRow
            ? Math.abs(savedCy - liveForRow.actualCy) > 0.005
              || Math.abs(savedPy - liveForRow.actualPy) > 0.005
            : false;
          return (
            <tr key={i} style={isLockedRow ? computedRowStyle : undefined}>
              {columns.map(c => {
                if (labelColumns.includes(c)) {
                  return (
                    <td key={c.code} style={{ ...tdStyle(c), fontWeight: isLockedRow ? 600 : 500 }}>
                      {String(row[c.code] ?? '')}
                      {rowHasDrift && liveForRow && (
                        <span
                          style={driftChipStyle}
                          title={
                            `Saved value differs from current source.\n`
                            + `Saved CY: ${savedCy.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`
                            + `Live CY: ${liveForRow.actualCy.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`
                            + `Use "Update from Sources" on the pane header to refresh.`
                          }
                        >
                          ↻ drift
                        </span>
                      )}
                    </td>
                  );
                }
                // Effective data type for this cell — row override wins
                // over column default.
                const effectiveDataType = rowDataType ?? c.data_type;
                // Amount columns always use the dual-storage editor.
                // Computed rows (e.g. Closing balance) lock the actual
                // input (formula owns it) but keep the rounded input
                // EDITABLE so the auditor can override the auto-summed
                // rounded total — matches the audit override pattern the
                // user called out.
                if (effectiveDataType === 'amount') {
                  const actual = typeof row[c.code] === 'number' ? (row[c.code] as number) : 0;
                  // effectiveFactor: rounding_exempt rows skip the
                  // engagement factor (no THOUSANDS/LAKHS divide), but
                  // the rounded slot is still 2dp-rounded — it's NOT
                  // the raw actual with full precision (which can be
                  // 16787.817265625 for a computed EPS).
                  const effectiveFactor: RoundingFactor = rowRoundingExempt ? 'ACTUALS' : factor;
                  const rounded = typeof row[c.code + ROUNDED_SUFFIX] === 'number'
                    ? (row[c.code + ROUNDED_SUFFIX] as number)
                    : autoRound(actual, effectiveFactor);
                  const tooltip = fr.value_source
                    ? `Pulled from ${fr.value_source.report_code ?? ''} · ${fr.value_source.line_code ?? ''}`
                    : isComputedRow
                      ? `Computed: ${fr.formula}`
                      : undefined;
                  return (
                    <td
                      key={c.code}
                      style={tdStyle(c)}
                      title={tooltip}
                    >
                      <AmountCellEditor
                        actualValue={actual}
                        roundedValue={rounded}
                        factor={effectiveFactor}
                        tolerance={tolerance}
                        // Lock the actual input ONLY when the row is
                        // is_computed (formula owns the value) or
                        // value_source (backend pulls the value), or the
                        // column is template-computed. rounding_exempt
                        // alone does NOT lock — it only suppresses the
                        // factor (face value, EPS rates are auditor-
                        // editable inputs that just don't get divided).
                        isComputed={isLockedRow || c.is_computed === true}
                        readOnly={readOnly}
                        onChange={(a, r) => handleAmountCellChange(i, c, a, r)}
                        factorHint={rowRoundingExempt ? undefined : factorUnits}
                        displayMode={displayMode}
                      />
                    </td>
                  );
                }
                if (isLockedRow) {
                  const v = typeof row[c.code] === 'number' ? (row[c.code] as number) : 0;
                  return (
                    <td key={c.code} style={{ ...tdStyle(c), fontWeight: 700 }}>
                      <span style={computedCellStyle} title={`Computed: ${fr.formula}`}>
                        {formatByType(effectiveDataType, v)}
                      </span>
                    </td>
                  );
                }
                // Editable non-amount cell — honor per-row data_type
                // override when it differs from the column default.
                const effectiveSpec = rowDataType && rowDataType !== c.data_type
                  ? { ...c, data_type: rowDataType }
                  : c;
                return (
                  <td key={c.code} style={tdStyle(c)}>
                    <CellEditor
                      spec={effectiveSpec}
                      value={row[c.code]}
                      onChange={v => handleCellChange(i, c, v)}
                      readOnly={readOnly}
                    />
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
    <DriftBadgeList results={validationResults} />
    </>
  );
};

// ───── recompute: derive every value the PDF/display reads from JSON ──────
//
// Strict rule (Resources/info 2.txt §1): the renderer never computes —
// every derived value lives in the JSON. Recompute handles that
// canonicalisation during edit so saved data is print-ready.
//
// Two passes:
//   1. Computed rows: for each data column, evaluate the formula against
//      the canonical `actual` values to produce `<col>`. For amount
//      columns, evaluate the SAME formula against the source rows'
//      `__rounded` values to produce `<col>__rounded` — gives the
//      auditor "sum of rounded" (matches Schedule III print convention)
//      instead of auto-round-of-sum (which can drift from line totals).
//      If a row's `__rounded` companion is missing, the resolver falls
//      back to autoRound(actual, factor) so legacy rows still produce a
//      number — that fallback writes the missing companion as well, so
//      the very first edit migrates the row to dual storage.
//   2. Editable amount rows: reconcile `<col>__rounded` from `<col>` via
//      autoRound + tolerance preserve. Trusts a prior user override.

/**
 * Convenience wrapper for callers that have the disclosure spec
 * (columns + fixedRows) and rows but don't want to manually derive
 * `dataColumns` / `rowKeysInOrder` themselves. Mirrors the derivation
 * the component does internally. Used by the disclosures pane's
 * "Update from Sources" action — after overwriting value_source row
 * values from the live snapshot, this re-runs the formula pipeline
 * so `basic_eps`/`diluted_eps` (and any other computed rows) reflect
 * the refreshed operands in the same click.
 */
export function recomputeFixedGridFromSpec(
  rows: Row[],
  columns: ColumnSpec[],
  fixedRows: FixedRow[],
  factor: RoundingFactor | null | undefined,
  tolerance: number = DEFAULT_ROUNDING_TOLERANCE,
  /**
   * When TRUE, computed rows' rounded slots are written DIRECTLY to the
   * fresh formula(rounded operands) value, bypassing the tolerance
   * preservation that normally honours an auditor's manual rounded
   * override. Used by the disclosures pane's "Update from Sources"
   * action — the auditor explicitly asked for a refresh, so a previous
   * tolerance-preserved value (e.g. basic_eps rounded stuck at 16.78
   * when the formula on fresh operands now yields 16.79) must be
   * discarded. Defaults to FALSE so per-cell edit handlers keep their
   * normal override semantics.
   */
  forceFreshComputed: boolean = false,
): Row[] {
  const labelColumns = columns.filter(
    c => c.data_type === 'text' && /particular|category/i.test(c.code),
  );
  const dataColumns = columns.filter(c => !labelColumns.includes(c));
  const rowKeysInOrder = fixedRows.map(fr => deriveRowKey(fr));
  return recomputeFixedGrid(
    rows,
    fixedRows,
    dataColumns,
    rowKeysInOrder,
    factor ?? 'ACTUALS',
    tolerance,
    forceFreshComputed,
  );
}

/**
 * Re-evaluates every is_computed row's formula against the current
 * operand values + reconciles each amount cell's __rounded companion.
 * Used by both the grid's per-cell edit handlers AND the disclosures
 * pane's "Update from Sources" action so a value_source refresh
 * cascades into the formulas downstream (e.g. EPS profit changing
 * updates basic_eps + diluted_eps in the same click).
 */
export function recomputeFixedGrid(
  rows: Row[],
  fixedRows: FixedRow[],
  dataColumns: ColumnSpec[],
  rowKeysInOrder: string[],
  factor: RoundingFactor = 'ACTUALS',
  tolerance: number = DEFAULT_ROUNDING_TOLERANCE,
  /** See recomputeFixedGridFromSpec for semantics. */
  forceFreshComputed: boolean = false,
): Row[] {
  const next = rows.map(r => ({ ...r }));

  // Resolver helper: fetches a numeric value for a formula identifier.
  // `targetKey` says which companion to read (e.g. col.code for actual,
  // col.code + '__rounded' for rounded). Same formula, two passes,
  // different source keys.
  const resolveFormula = (
    formula: string,
    currentRowIdx: number,
    targetKey: (col: ColumnSpec) => string,
    c: ColumnSpec,
  ): number =>
    evaluateFormula(formula, name => {
      if (name.startsWith('row_')) {
        const token = name.slice(4);
        const idx = rowKeysInOrder.indexOf(token);
        if (idx === -1) return undefined;
        const v = next[idx]?.[targetKey(c)];
        return typeof v === 'number' ? v : Number(v) || 0;
      }
      const own = next[currentRowIdx]?.[name];
      return typeof own === 'number' ? own : Number(own) || 0;
    });

  // Pass 1 — computed rows. Implements the user-stated principle:
  // "the calculated values are calculated using rounded figures in
  // rounded view." So the rounded companion ALWAYS comes from evaluating
  // the formula against operand rounded values — independent of the
  // computed row's own rounding_exempt flag. The actual is rounded to
  // 2dp for display sanity (e.g. EPS = 4297681.22 / 256 must display
  // 16787.82, not 16787.817265625).
  for (let i = 0; i < fixedRows.length; i++) {
    const fr = fixedRows[i];
    if (!fr.is_computed || !fr.formula) continue;
    for (const c of dataColumns) {
      // Actual: evaluate against the canonical column code, round to 2dp.
      const computedActualRaw = resolveFormula(fr.formula, i, col => col.code, c);
      const computedActual = c.data_type === 'amount'
        ? Math.round(computedActualRaw * 100) / 100
        : computedActualRaw;
      next[i][c.code] = computedActual;

      // Rounded: seed any missing __rounded operand from its row's
      // effective factor, then evaluate the formula against rounded
      // operands. This way rounded view = "calculate using rounded
      // figures" — auditor's typed rounded values (or rounding-exempt
      // rounded === actual) flow naturally into the computed row's
      // rounded slot.
      if (c.data_type === 'amount') {
        for (let s = 0; s < next.length; s++) {
          if (typeof next[s][c.code + ROUNDED_SUFFIX] !== 'number') {
            const srcFr = fixedRows[s];
            const srcRowFactor = effectiveFactorForRow(srcFr, factor);
            const a = typeof next[s][c.code] === 'number' ? (next[s][c.code] as number) : 0;
            next[s][c.code + ROUNDED_SUFFIX] = autoRound(a, srcRowFactor);
          }
        }
        const newRounded = Math.round(
          resolveFormula(fr.formula, i, col => col.code + ROUNDED_SUFFIX, c) * 100,
        ) / 100;
        if (forceFreshComputed) {
          // Explicit-refresh path (Update from Sources): skip tolerance
          // preservation so a previously-stored rounded close to the
          // fresh formula value (within tolerance) doesn't shadow the
          // recalculation. Auditor's manual rounded override on a
          // computed row is intentionally discarded here.
          next[i][c.code + ROUNDED_SUFFIX] = newRounded;
        } else {
          const stored = typeof next[i][c.code + ROUNDED_SUFFIX] === 'number'
            ? (next[i][c.code + ROUNDED_SUFFIX] as number)
            : newRounded;
          next[i][c.code + ROUNDED_SUFFIX] = applyRoundingTolerance(stored, newRounded, tolerance);
        }
      }
    }
  }

  // Pass 2 — editable amount rows: reconcile __rounded from actual.
  // Per-row effective factor — integer / rounding_exempt rows skip the
  // engagement-factor divide and store __rounded = actual.
  for (let i = 0; i < next.length; i++) {
    const fr = fixedRows[i];
    if (fr?.is_computed === true) continue; // pass 1 owns these
    const rowFactor = effectiveFactorForRow(fr, factor);
    for (const c of dataColumns) {
      if (c.data_type !== 'amount') continue;
      const actual = typeof next[i][c.code] === 'number' ? (next[i][c.code] as number) : 0;
      const auto = autoRound(actual, rowFactor);
      const stored = typeof next[i][c.code + ROUNDED_SUFFIX] === 'number'
        ? (next[i][c.code + ROUNDED_SUFFIX] as number)
        : auto;
      next[i][c.code + ROUNDED_SUFFIX] = applyRoundingTolerance(stored, auto, tolerance);
    }
  }

  return next;
}

// ───── cell editor (numeric only — labels are read-only via the spec) ──────

const CellEditor: React.FC<{
  spec: ColumnSpec;
  value: string | number | null | undefined;
  onChange: (v: string | number | null) => void;
  readOnly: boolean;
}> = ({ spec, value, onChange, readOnly }) => {
  switch (spec.data_type) {
    case 'amount':
    case 'integer':
    case 'percent': {
      const numeric = typeof value === 'number' ? value : Number(value) || 0;
      const input = (
        <input
          type="number"
          className="form-input"
          value={numeric}
          step={spec.data_type === 'integer' ? '1' : '0.01'}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={e => onChange(Number(e.target.value))}
          style={spec.data_type === 'percent' ? numericInputPercentStyle : numericInputStyle}
        />
      );
      if (spec.data_type === 'percent') {
        return (
          <span style={percentWrapStyle}>
            {input}
            <span style={percentSuffixStyle}>%</span>
          </span>
        );
      }
      return input;
    }
    case 'enum':
      return (
        <select
          className="form-input"
          value={(value ?? '') as string}
          disabled={readOnly}
          onChange={e => onChange(e.target.value)}
          style={inputBaseStyle}
        >
          <option value="">—</option>
          {(spec.options ?? []).map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    case 'date':
      return (
        <input
          type="date"
          className="form-input"
          value={(value ?? '') as string}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={e => onChange(e.target.value)}
          style={inputBaseStyle}
        />
      );
    case 'text':
    default:
      return (
        <input
          type="text"
          className="form-input"
          value={(value ?? '') as string}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={e => onChange(e.target.value)}
          style={inputBaseStyle}
        />
      );
  }
};

function formatByType(dataType: string, v: number): string {
  if (dataType === 'amount') {
    return v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  if (dataType === 'integer') {
    return v.toLocaleString('en-IN');
  }
  if (dataType === 'percent') {
    return `${v.toFixed(2)}%`;
  }
  return String(v);
}

// ───── styles ────────────────────────────────────────────────────────────────

const gridTableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.78rem',
  tableLayout: 'fixed',
  wordBreak: 'break-word',
};
const thBaseStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '6px 10px',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: 0.4,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
};
const tdBaseStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid var(--border-color)',
  verticalAlign: 'middle',
};
const thStyle = (c: ColumnSpec): React.CSSProperties => ({
  ...thBaseStyle,
  textAlign: c.align === 'right' ? 'right' : 'left',
});
// Style for the spanning "Current Year" / "Previous Year" group header
// cells. Centered, slightly tinted so the group band stays visually
// distinct from the metric-column labels underneath.
const thGroupStyle: React.CSSProperties = {
  ...thBaseStyle,
  textAlign: 'center',
  background: 'var(--bg-tertiary, #e2e8f0)',
};
const tdStyle = (c: ColumnSpec): React.CSSProperties => ({
  ...tdBaseStyle,
  textAlign: c.align === 'right' ? 'right' : 'left',
});
const computedRowStyle: React.CSSProperties = {
  background: 'var(--bg-secondary)',
};
const computedChipStyle: React.CSSProperties = {
  marginLeft: 4,
  color: '#d97706',
  fontWeight: 700,
};
// Drift chip — small amber pill next to a value_source row's label when
// its saved value differs from the live source-Statement value. Same
// amber tone as the MainNote's ledger-drift chip for visual consistency.
const driftChipStyle: React.CSSProperties = {
  marginLeft: 8,
  fontSize: '0.66rem',
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 9,
  background: '#fef3c7',
  color: '#92400e',
  border: '1px solid #fde68a',
  whiteSpace: 'nowrap',
};
const computedCellStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
};
const numericInputStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '2px 6px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
const numericInputPercentStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '2px 16px 2px 6px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};
const percentWrapStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-block',
  width: '100%',
};
const percentSuffixStyle: React.CSSProperties = {
  position: 'absolute',
  right: 6,
  top: '50%',
  transform: 'translateY(-50%)',
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  pointerEvents: 'none',
};

const inputBaseStyle: React.CSSProperties = {
  width: '100%',
  fontSize: '0.78rem',
  padding: '2px 6px',
};

export default DisclosureFixedGrid;
